import { CoreControllerInput } from '@ingenico/react-ui/core-form';
import { type FileWithStatus, FileSelect, getHumanizedFileSize } from '@ingenico/react-ui/file-handlerV2';
import { FormFieldContainer } from '@ingenico/react-ui/form-field-container';
import type { AutocompleteDeleteEvent } from '@ingenico/react-ui/form-section';
import { AutocompleteControl } from '@ingenico/react-ui/form-section';
import { EditorMode, MarkDownEditorCtrl } from '@ingenico/react-ui/mark-down-editor';
import { TextFieldInputFwd } from '@ingenico/react-ui/text-field-input';

import { ALL_LNGS_NAMESPACES } from '@ingenico/util-i18n-config';
import { CoreButton } from '@ingenico/react-ui/core-button';
import { CoreTypography, CoreTypographyVariant } from '@ingenico/react-ui/core-typography';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { FormControl } from '@mui/material';
import { useDebouncedCallback, useUpdateEffect } from '@react-hookz/web';
import classNames from 'classnames';
import { useSetAtom } from 'jotai';
import { isEmpty, isNumber, isString, noop, toArray, toString, uniqueId } from 'lodash-es';
import type { ChangeEvent, PropsWithChildren, ReactNode, SyntheticEvent } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Control, ControllerRenderProps } from 'react-hook-form';
import { Controller, useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import {
  appFilterAtom,
  appVersionFilterAtom,
  operationalSystemFilterAtom,
  operationalSystemVersionFilterAtom,
  systemPackageFilterAtom,
  systemPackageVersionFilterAtom,
} from '../../atoms/applicationVersionFilters.atom';
import { SystemPackageArtifactType } from '../../domain/models/systemPackage.model';
import type {
  ParameterTemplate,
  SystemPackageAppVersion,
  TargetLocationError,
} from '../../pages/create-system-package-version-page/createSystemPackageVersionPage.model';
import { AppVersionFileUploadErrorStatus } from '../../pages/create-system-package-version-page/createSystemPackageVersionPage.model';
import filePreviewStyles from '../../styles/file-preview-truncate.module.scss';
import {
  createUploadedFileList,
  getDeploymentRulesFieldError,
  getSumUploadedTemplates,
  verifyTemplatesFilesWereUploaded,
} from '../application-version-fields/applicationVersionFields.util';
import { DeviceAppVersionFilesList } from '../device-app-version-files-list/DeviceAppVersionFilesList.ctrl';
import { DeviceAppVersionUploadSummary } from '../device-app-version-upload-summary/DeviceAppVersionUploadSummary';
import {
  getFileExtensionFromFilename,
  isFileExtensionRequiredForTargetLocation,
  verifyIsSystemPackageVersionDeploymentRulesError,
} from '../system-package-version-steps/utils/systemPackageVersionSteps.util';
import { templateInfoJoiner } from '../system-package-version-summary-step/systemPackageVersionSummaryStep.util';
import { resolveFileUploadErrorMessage } from '../../utils/fileUploadErrorMessage.util';
import type {
  SystemPackageVersionAutocompleteFieldProps,
  SystemPackageVersionAutocompleteFieldValue,
  SystemPackageVersionCommonInputFieldProps,
  SystemPackageVersionDeploymentRulesFieldProps,
  SystemPackageVersionDeploymentRulesFieldResource,
  SystemPackageVersionSelectFieldProps,
  SystemPackageVersionSystemPackageFilesFieldProps,
  SystemPackageVersionUploadTemplatesFieldProps,
} from './systemPackageVersionFields.model';
import styles from './systemPackageVersionFields.module.scss';
import { MarkDownEditorContainer } from '@ingenico/react-ui/mark-down-editor-container';

function getInputChangeValue(value: string, reason: string): string {
  return reason === 'input' ? value : '';
}

export function SystemPackageVersionNameField({
  control,
  readOnly,
  required,
  disabled,
}: PropsWithChildren<SystemPackageVersionCommonInputFieldProps>): ReactNode {
  const { t } = useTranslation(ALL_LNGS_NAMESPACES.APPLICATION);

  return (
    <div className={styles.textInputWrapper}>
      <CoreControllerInput
        control={control as unknown as Control}
        dataTestId="app-version-version-field"
        disabled={disabled}
        helperText={t('input.version.helperText')}
        label={t('input.version.label')}
        name="definition.version"
        placeholder={t('input.version.placeholder')}
        readOnly={readOnly}
        required={required}
      />
    </div>
  );
}

export function SystemPackageVersionReleaseNotesField({
  control,
  disabled,
  readOnly,
}: PropsWithChildren<SystemPackageVersionCommonInputFieldProps>): ReactNode {
  const { t } = useTranslation(ALL_LNGS_NAMESPACES.APPLICATION);

  const { field, fieldState } = useController({ control, name: 'definition.releaseNotes' });

  const handleChange = useCallback((value?: string) => field.onChange(value), [field]);
  const handleBlur = useCallback(() => field.onBlur(), [field]);

  const { error, helperText } = useMemo(() => {
    return {
      error: fieldState.invalid,
      helperText: fieldState.error?.message,
    };
  }, [fieldState.error?.message, fieldState.invalid]);

  const customClassName = useMemo(() => {
    return classNames({ [styles.releaseNotes__error__container]: error });
  }, [error]);
  const customFontClassName = useMemo(() => {
    return classNames({ [styles.releaseNotes__error__font]: error });
  }, [error]);
  return (
    <div>
      <CoreTypography
        customClassName={classNames(styles.releaseNotes__label, customFontClassName)}
        variant={CoreTypographyVariant.Body03Regular}
      >
        {t('input.releaseNotes.label', { suffix: '*' })}
      </CoreTypography>
      <MarkDownEditorContainer>
        <MarkDownEditorCtrl
          content={toString(field.value)}
          mode={disabled || readOnly ? EditorMode.Preview : EditorMode.Editor}
          onChange={handleChange}
          onBlur={handleBlur}
          customClassName={customClassName}
          customFontClassName={customFontClassName}
        />
      </MarkDownEditorContainer>
      {error && (
        <CoreTypography
          customClassName={classNames(styles.releaseNotes__label, customFontClassName)}
          variant={CoreTypographyVariant.Body03Regular}
        >
          {helperText}
        </CoreTypography>
      )}
    </div>
  );
}

export function SystemPackageVersionDropdownField({
  control,
  label,
  disabled,
  readOnly,
  required,
  dataTestId,
  isLoading,
  options,
  name,
}: Omit<SystemPackageVersionAutocompleteFieldProps, 'setFilter'>): ReactNode {
  const setAppFilter = useSetAtom(appFilterAtom);
  const setSystemPackageFilter = useSetAtom(systemPackageFilterAtom);
  const setOperationalSystemFilter = useSetAtom(operationalSystemFilterAtom);

  // Create debounced versions of each filter function
  const debouncedSetAppFilter = useDebouncedCallback((value: string) => setAppFilter(value), [], 500);

  const debouncedSetSystemPackageFilter = useDebouncedCallback(
    (value: string) => setSystemPackageFilter(value),
    [],
    500,
  );

  const debouncedSetOperationalSystemFilter = useDebouncedCallback(
    (value: string) => setOperationalSystemFilter(value),
    [],
    500,
  );

  const getSetFilterFunction = useCallback(
    (fieldName: string) => {
      if (fieldName.includes('appDependency')) {
        return debouncedSetAppFilter;
      }
      if (fieldName.includes('systemPackageDependency')) {
        return debouncedSetSystemPackageFilter;
      }
      if (fieldName.includes('operationalSystemDependency')) {
        return debouncedSetOperationalSystemFilter;
      }
      // Return a no-op function for unknown field names
      return noop;
    },
    [debouncedSetAppFilter, debouncedSetSystemPackageFilter, debouncedSetOperationalSystemFilter],
  );

  const handleChange = useCallback(
    (
      field: ControllerRenderProps<
        SystemPackageAppVersion,
        'definition.operationalSystemDependency' | 'definition.systemPackageDependency' | 'definition.appDependency'
      >,
    ) =>
      (_: SyntheticEvent, data: SystemPackageVersionAutocompleteFieldValue | null) => {
        field.onChange(data?.value ?? undefined);
      },
    [],
  );

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState, formState }) => {
        const setFilter = getSetFilterFunction(field.name);

        return (
          <FormFieldContainer
            customClassName={classNames({
              [styles.appVersionAutocompleteContainer__valid]: !fieldState.invalid,
            })}
          >
            <AutocompleteControl<SystemPackageVersionAutocompleteFieldValue, false>
              {...formState}
              {...fieldState}
              {...field}
              disabled={disabled && !readOnly}
              error={fieldState.invalid}
              filterSelectedOptions={false}
              formHelperTextProps={{
                id: `field-error-app-version-dropdown-${dataTestId}`,
              }}
              getOptionLabel={(option: SystemPackageVersionAutocompleteFieldValue | string) => {
                if (isString(option)) {
                  return option;
                }
                return toString(option?.label);
              }}
              helperText={fieldState.error?.message}
              input={{
                label,
                name: field.name,
                component: 'Autocomplete',
              }}
              inputProps={{
                'data-testid': `field-app-version-dropdown-${dataTestId}`,
              }}
              onInputChange={(_: unknown, value: string, reason: string) => {
                setFilter(getInputChangeValue(value, reason));
              }}
              loading={isLoading}
              onChange={handleChange(field)}
              options={options ?? []}
              readOnly={readOnly}
              required={required}
              value={options?.find(({ value }) => value === field.value) ?? null}
            />
          </FormFieldContainer>
        );
      }}
    />
  );
}

export function SystemPackageVersionSelectField({
  control,
  label,
  disabled,
  readOnly,
  required,
  dataTestId,
  isLoading,
  options,
  name,
  clearFieldError,
}: SystemPackageVersionSelectFieldProps): ReactNode {
  const { t } = useTranslation([ALL_LNGS_NAMESPACES.APPLICATION, ALL_LNGS_NAMESPACES.COMMON]);
  const { field, fieldState, formState } = useController({ name, control, defaultValue: [] });
  const translateChips = useCallback(
    ({ label: chipLabel }: SystemPackageVersionAutocompleteFieldValue) => chipLabel,
    [],
  );
  const currentValue = useMemo(() => toArray(field.value), [field.value]);

  const setAppVersionFilter = useSetAtom(appVersionFilterAtom);
  const setSystemPackageVersionFilter = useSetAtom(systemPackageVersionFilterAtom);
  const setOperationalSystemVersionFilter = useSetAtom(operationalSystemVersionFilterAtom);

  // Create debounced versions of each filter function
  const debouncedSetAppVersionFilter = useDebouncedCallback((value: string) => setAppVersionFilter(value), [], 500);

  const debouncedSetSystemPackageVersionFilter = useDebouncedCallback(
    (value: string) => setSystemPackageVersionFilter(value),
    [],
    500,
  );

  const debouncedSetOperationalSystemVersionFilter = useDebouncedCallback(
    (value: string) => setOperationalSystemVersionFilter(value),
    [],
    500,
  );

  const getSetVersionFilterFunction = useCallback(
    (fieldName: string) => {
      if (fieldName.includes('minimumAppVersion')) {
        return debouncedSetAppVersionFilter;
      }
      if (fieldName.includes('minimumSystemPackageVersion')) {
        return debouncedSetSystemPackageVersionFilter;
      }
      if (fieldName.includes('minimumOSVersion')) {
        return debouncedSetOperationalSystemVersionFilter;
      }
      // Return a no-op function for unknown field names
      return () => {
        /* no-op */
      };
    },
    [debouncedSetAppVersionFilter, debouncedSetSystemPackageVersionFilter, debouncedSetOperationalSystemVersionFilter],
  );
  const setFilter = getSetVersionFilterFunction(field.name);

  const isOptionEqualToValue = useCallback(
    (option: SystemPackageVersionAutocompleteFieldValue, value: SystemPackageVersionAutocompleteFieldValue) =>
      option?.value === value?.value,
    [],
  );

  const getOptionLabel = useCallback((option: SystemPackageVersionAutocompleteFieldValue | string) => {
    return typeof option === 'string' ? option : option.label;
  }, []);

  const handleDeleteChips = useCallback(
    ({ index: indexToBeRemoved }: AutocompleteDeleteEvent) => {
      field.onChange(
        field.value?.filter(
          (_: SystemPackageVersionAutocompleteFieldValue, index: number) => index !== indexToBeRemoved,
        ),
      );
    },
    [field],
  );

  const handleFieldChange = useCallback(
    (_: unknown, data: SystemPackageVersionAutocompleteFieldValue[]) => {
      field.onChange(data ?? undefined);
      if (data) {
        clearFieldError?.('build.applicationFiles');
        clearFieldError?.('build.existingAppFiles');
      }
    },
    [field, clearFieldError],
  );

  return (
    <FormFieldContainer
      customClassName={classNames({
        [styles.appVersionAutocompleteContainer__valid]: verifyIsSystemPackageVersionDeploymentRulesError(
          fieldState.error,
        )
          ? true
          : !fieldState.invalid,
      })}
    >
      <AutocompleteControl
        {...formState}
        {...fieldState}
        {...field}
        disableClearable={false}
        disabled={disabled && !readOnly}
        error={verifyIsSystemPackageVersionDeploymentRulesError(fieldState.error) ? false : fieldState.invalid}
        formHelperTextProps={{
          id: `field-select-${label?.replace(/\s/u, '-')}`,
        }}
        getOptionLabel={getOptionLabel}
        helperText={
          verifyIsSystemPackageVersionDeploymentRulesError(fieldState.error) ? undefined : fieldState.error?.message
        }
        input={{
          label,
          name: field.name,
          component: 'Autocomplete',
        }}
        inputProps={{
          'data-testid': dataTestId,
        }}
        onInputChange={(_: unknown, value: string, reason: string) => {
          setFilter(getInputChangeValue(value, reason));
        }}
        isOptionEqualToValue={isOptionEqualToValue}
        loading={isLoading}
        loadingText={t('autocomplete.loadingText', { ns: ALL_LNGS_NAMESPACES.COMMON })}
        multiple
        noOptionsText={t('select.noOptions', { ns: ALL_LNGS_NAMESPACES.COMMON })}
        onChange={handleFieldChange}
        onDelete={handleDeleteChips}
        options={options}
        renderTagsReadOnlyMode={translateChips}
        renderTagsTranslateLabel={translateChips}
        required={required}
        value={currentValue}
      />
    </FormFieldContainer>
  );
}

export function SystemPackageVersionUploadTemplatesField({
  control,
  disabled,
  clearFieldError,
  setFieldError,
  maxFileSize,
  fileExtensions,
  unitFileSize,
  getUploadingProgress,
}: SystemPackageVersionUploadTemplatesFieldProps): ReactNode {
  const [errorIndexes, setErrorIndexes] = useState<number[]>([]);
  const { t } = useTranslation([ALL_LNGS_NAMESPACES.APPLICATION, ALL_LNGS_NAMESPACES.COMMON]);

  const { field, fieldState } = useController({ name: 'configure.parameterTemplates', control, defaultValue: [] });

  useUpdateEffect(() => {
    if (errorIndexes.length === 0) {
      clearFieldError?.('configure.parameterTemplates');
    }
  }, [errorIndexes]);

  const getEmptyTemplateConfig = useCallback(
    () => ({
      targetLocation: '',
      template: [],
      id: uniqueId(),
    }),
    [],
  );

  useEffect(() => {
    const indexesInError = field.value?.reduce(
      (accum, current, index) => [...accum, ...(current.template.some(({ error }) => error) ? [index] : [])],
      [] as number[],
    );
    setErrorIndexes(indexesInError);
    if (indexesInError.length > 0) {
      const firstError = field.value
        ?.flatMap(({ template }) => template)
        ?.find(({ error, messageError }) => error && messageError)?.messageError;
      setFieldError?.('configure.parameterTemplates', firstError ?? t('genericError.invalidFormat'));
    }
  }, [field.value, setFieldError, t]);

  const clearParameterErrors = useCallback((index?: number) => {
    setErrorIndexes((current) => (index === undefined ? [] : current.filter((idx) => idx !== index)));
  }, []);

  const clearAndRecalcErrors = useCallback((index?: number) => {
    if (index !== undefined) {
      setErrorIndexes((current) => {
        const indexesToRecalc = current.filter((idx) => idx !== index);
        return indexesToRecalc.map((idx) => (idx > index ? idx - 1 : idx));
      });
    }
  }, []);
  const removeTemplate = useCallback(
    (idx?: number) => () => {
      const newFieldValue = field.value.filter((_, templateIndex) => templateIndex !== idx);
      clearAndRecalcErrors(idx);
      field.onChange([...newFieldValue]);
    },
    [field, clearAndRecalcErrors],
  );

  const manageFileChange = useCallback(
    (files: FileWithStatus[], idx?: number) => {
      clearParameterErrors(idx);

      if (isEmpty(files)) {
        if (isNumber(idx)) {
          removeTemplate(idx)();
        }

        return;
      }

      const displayedSize = `${maxFileSize} ${unitFileSize}`;
      const fieldValue = files.map((file, fileIndex) => ({
        checksum: files[fileIndex].hash,
        error: !!files[fileIndex].error,
        extension: files[fileIndex].extension,
        file: file.file,
        id: uuidv4(),
        messageError: resolveFileUploadErrorMessage(files[fileIndex].error ?? '', t, displayedSize),
        name: files[fileIndex].name,
        parsedTemplateId: files[fileIndex].parsedTemplateId,
        size: files[fileIndex].size,
        status: files[fileIndex].status,
        uploadStatus: AppVersionFileUploadErrorStatus.None,
        type: SystemPackageArtifactType.Template,
      }));

      if (!isNumber(idx)) {
        field.onChange([{ ...getEmptyTemplateConfig(), template: fieldValue }]);
        return;
      }
      const newFieldValue = field.value.map((templateParams, parameterIndex) => {
        return parameterIndex === idx
          ? {
              ...templateParams,
              template: fieldValue,
            }
          : templateParams;
      });
      field.onChange(newFieldValue);
    },
    [clearParameterErrors, field, removeTemplate, getEmptyTemplateConfig, t],
  );

  const handleFilesChange = useCallback(
    (idx?: number) => (files: FileWithStatus[]) => {
      manageFileChange(files, idx);
    },
    [manageFileChange],
  );

  const getCurrentDisplayedState = useCallback(
    (currentValues: ParameterTemplate[]) => {
      return currentValues.length > 0 ? currentValues : [getEmptyTemplateConfig()];
    },
    [getEmptyTemplateConfig],
  );

  const handleAddTemplateConfig = useCallback(() => {
    field.onChange([...getCurrentDisplayedState(field.value), getEmptyTemplateConfig()]);
  }, [field, getEmptyTemplateConfig, getCurrentDisplayedState]);

  const handleChangeTargetLocationField = useCallback(
    (idx?: number) => (evt: ChangeEvent<HTMLInputElement>) => {
      const newFieldValue = field.value.map((templateInfo, templateIndex) => {
        return templateIndex === idx
          ? {
              ...templateInfo,
              targetLocation: toString(evt.target.value),
              template: templateInfo.template.map((template) => ({
                uploadStatus: AppVersionFileUploadErrorStatus.None,
                uploadId: undefined,
                ...template,
              })),
            }
          : templateInfo;
      });
      field.onChange(newFieldValue);
    },
    [field],
  );

  const getUploadedFiles = useCallback(
    (idx?: number) => createUploadedFileList(toArray(field.value?.[idx ?? 0]?.template)),
    [field],
  );

  const checkIsInvalidParamByIndex = useCallback(
    (index?: number) => {
      return index === undefined ? fieldState.invalid : errorIndexes.includes(index);
    },
    [errorIndexes, fieldState.invalid],
  );

  const getParameterIsInvalid = useCallback(
    (index?: number) => {
      return fieldState.invalid && checkIsInvalidParamByIndex(index);
    },
    [fieldState, checkIsInvalidParamByIndex],
  );

  const getTemplateInfo = useCallback(
    (idx?: number, parameterTemplate?: ParameterTemplate) => {
      const currentIndex: number = idx ?? 0;
      return (
        <div className={styles.uploadTemplates__parameter} key={parameterTemplate?.id ?? 'no-template'}>
          <div className={styles.uploadTemplates__titleContainer}>
            <CoreTypography
              variant={CoreTypographyVariant.Title03Medium}
            >{`${t('input.parameterTemplates.helperText')} ${currentIndex + 1}`}</CoreTypography>
            {idx !== undefined && (
              <CoreButton onClick={removeTemplate(idx)} size="small">
                <CloseIcon />
              </CoreButton>
            )}
          </div>
          {verifyTemplatesFilesWereUploaded(field.value?.[currentIndex]?.template, idx) ||
          !!field.value?.[currentIndex]?.targetLocation ? (
            <TextFieldInputFwd
              data-testid="target-location"
              disabled={disabled}
              error={!!(fieldState?.error as unknown as TargetLocationError[])?.[currentIndex]?.targetLocation}
              fullWidth
              helperText={toString(
                (fieldState?.error as unknown as TargetLocationError[])?.[currentIndex]?.targetLocation?.message,
              )}
              label={t('input.parameterTemplates.label')}
              onChange={handleChangeTargetLocationField(idx)}
              required
              value={toString(field.value?.[currentIndex]?.targetLocation)}
              variant="filled"
              slotProps={{
                inputLabel: { shrink: true },
                htmlInput: { readOnly: disabled, 'data-testid': 'field-target-location' },
              }}
            />
          ) : undefined}
          <div className={filePreviewStyles.fileControlWrapper}>
            <FileSelect
              fileConfiguration={{
                isMultiFiles: false,
                autoCrop: true,
                maxFileSize,
                fileExtensions,
                minFileSize: 1,
                unitFileSize,
                maxFiles: 1,
              }}
              filesToUpload={getUploadedFiles(idx)}
              onFilesChange={handleFilesChange(idx)}
              hasError={getParameterIsInvalid(idx)}
              getUploadingProgress={getUploadingProgress}
              shouldProcessBase64={false}
              selectMainTitleText={t('field.upload.selectMainTitleText', { ns: ALL_LNGS_NAMESPACES.COMMON })}
              selectFormatsText={t('field.upload.selectFormatsText', {
                ns: ALL_LNGS_NAMESPACES.COMMON,
                formats: fileExtensions.join(', '),
              })}
              selectSizeText={t('field.upload.selectSizeText', {
                ns: ALL_LNGS_NAMESPACES.COMMON,
                size: `${maxFileSize} ${unitFileSize}`,
              })}
              selectBrowseText={t('field.upload.selectBrowseText', { ns: ALL_LNGS_NAMESPACES.COMMON })}
              globalErrorMessageText={getParameterIsInvalid(idx) ? toString(fieldState.error?.message) : undefined}
            />
          </div>
        </div>
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      t,
      removeTemplate,
      field.value,
      disabled,
      handleChangeTargetLocationField,
      fieldState.error,
      maxFileSize,
      fileExtensions,
      unitFileSize,
      handleFilesChange,
      getUploadedFiles,
      getParameterIsInvalid,
    ],
  );

  return (
    <FormControl error={fieldState.invalid} required>
      <div className={styles.uploadTemplates__container}>
        {field.value.length > 0
          ? field.value.map((value, idx) => {
              return getTemplateInfo(idx, value);
            })
          : getTemplateInfo()}
      </div>
      <DeviceAppVersionUploadSummary
        hideSize={field.value.length <= 0}
        onAdd={handleAddTemplateConfig}
        size={getSumUploadedTemplates(field.value, t)}
      />
    </FormControl>
  );
}

export function SystemPackageVersionApplicationFilesField({
  control,
  setFieldError,
  clearFieldError,
  fileExtensions,
  maxFileSize,
  unitFileSize = 'MB',
  disabled,
  getUploadingProgress,
}: SystemPackageVersionSystemPackageFilesFieldProps): ReactNode {
  const fieldName = 'build.applicationFiles';
  const [errorIndexes, setErrorIndexes] = useState<number[]>([]);
  const { t } = useTranslation(ALL_LNGS_NAMESPACES.APPLICATION);

  const { field, fieldState } = useController({ name: fieldName, control, defaultValue: [] });

  useUpdateEffect(() => {
    if (errorIndexes.length === 0 && !verifyIsSystemPackageVersionDeploymentRulesError(fieldState.error)) {
      clearFieldError?.(fieldName);
    }
  }, [errorIndexes]);

  const getEmptyTemplateConfig = useCallback(
    () => ({
      targetLocation: '',
      template: [],
      id: uniqueId(),
    }),
    [],
  );

  useEffect(() => {
    const indexesInError = field.value?.reduce(
      (accum, current, index) => [...accum, ...(current.template.some(({ error }) => error) ? [index] : [])],
      [] as number[],
    );
    setErrorIndexes(indexesInError);
    if (indexesInError.length > 0) {
      const firstError = field.value
        ?.flatMap(({ template }) => template)
        ?.find(({ error, messageError }) => error && messageError)?.messageError;
      setFieldError?.(fieldName, firstError ?? t('genericError.invalidFormat'));
    }
  }, [field.value, setFieldError, t]);

  const clearParameterErrors = useCallback((index?: number) => {
    setErrorIndexes((current) => (index === undefined ? [] : current.filter((idx) => idx !== index)));
  }, []);

  const clearAndRecalcErrors = useCallback((index?: number) => {
    if (index !== undefined) {
      setErrorIndexes((current) => {
        const indexesToRecalc = current.filter((idx) => idx !== index);
        return indexesToRecalc.map((idx) => (idx > index ? idx - 1 : idx));
      });
    }
  }, []);
  const removeTemplate = useCallback(
    (idx?: number) => () => {
      const newFieldValue = field.value.filter((_, templateIndex) => templateIndex !== idx);
      clearAndRecalcErrors(idx);
      field.onChange([...newFieldValue]);
    },
    [field, clearAndRecalcErrors],
  );

  const displayedSize = useMemo(() => `${maxFileSize} ${unitFileSize}`, [maxFileSize, unitFileSize]);

  const manageFileChange = useCallback(
    (files: FileWithStatus[], idx?: number) => {
      clearParameterErrors(idx);

      if (isEmpty(files)) {
        if (isNumber(idx)) {
          removeTemplate(idx)();
        }

        return;
      }

      clearFieldError?.('build.existingAppFiles');

      const fieldValue = files.map((file, fileIndex) => ({
        checksum: files[fileIndex].hash,
        error: !!files[fileIndex].error,
        extension: files[fileIndex].extension,
        file: file.file,
        id: uuidv4(),
        messageError: resolveFileUploadErrorMessage(files[fileIndex].error ?? '', t, displayedSize),
        name: files[fileIndex].name,
        size: files[fileIndex].size,
        status: files[fileIndex].status,
        type: SystemPackageArtifactType.SwPackage,
        uploadStatus: AppVersionFileUploadErrorStatus.None,
      }));

      if (!isNumber(idx)) {
        field.onChange([{ ...getEmptyTemplateConfig(), template: fieldValue }]);
        return;
      }
      const newFieldValue = field.value.map((templateParams, parameterIndex) => {
        return parameterIndex === idx
          ? {
              ...templateParams,
              template: fieldValue,
            }
          : templateParams;
      });
      field.onChange(newFieldValue);
    },
    [clearParameterErrors, clearFieldError, field, removeTemplate, getEmptyTemplateConfig, displayedSize, t],
  );

  const handleFilesChange = useCallback(
    (idx?: number) => (files: FileWithStatus[]) => {
      manageFileChange(files, idx);
    },
    [manageFileChange],
  );

  const getCurrentDisplayedState = useCallback(
    (currentValues: ParameterTemplate[]) => {
      return currentValues.length > 0 ? currentValues : [getEmptyTemplateConfig()];
    },
    [getEmptyTemplateConfig],
  );

  const handleAddTemplateConfig = useCallback(() => {
    field.onChange([...getCurrentDisplayedState(field.value), getEmptyTemplateConfig()]);
  }, [field, getEmptyTemplateConfig, getCurrentDisplayedState]);

  const handleChangeTargetLocationField = useCallback(
    (idx?: number) => (evt: ChangeEvent<HTMLInputElement>) => {
      const newFieldValue = field.value.map((templateInfo, templateIndex) => {
        return templateIndex === idx
          ? {
              ...templateInfo,
              targetLocation: toString(evt.target.value),
              template: templateInfo.template.map((template) => ({
                uploadStatus: AppVersionFileUploadErrorStatus.None,
                uploadId: undefined,
                ...template,
              })),
            }
          : templateInfo;
      });
      field.onChange(newFieldValue);
    },
    [field],
  );

  const getUploadedFiles = useCallback(
    (idx?: number) => createUploadedFileList(toArray(field.value?.[idx ?? 0]?.template)),
    [field],
  );

  const checkIsInvalidParamByIndex = useCallback(
    (index?: number) => {
      return index === undefined ? fieldState.invalid : errorIndexes.includes(index);
    },
    [errorIndexes, fieldState.invalid],
  );

  const getParameterIsInvalid = useCallback(
    (index?: number) => {
      return fieldState.invalid && checkIsInvalidParamByIndex(index);
    },
    [fieldState, checkIsInvalidParamByIndex],
  );

  const isTargetFieldRequired = useCallback((fieldValue?: ParameterTemplate) => {
    if (isEmpty(fieldValue)) {
      return false;
    }
    const { template } = fieldValue;
    return isFileExtensionRequiredForTargetLocation(getFileExtensionFromFilename(toString(template?.[0]?.name)));
  }, []);

  const getTemplateInfo = useCallback(
    (idx?: number, parameterTemplate?: ParameterTemplate) => {
      const currentIndex: number = idx ?? 0;
      return (
        <div className={styles.uploadTemplates__parameter} key={parameterTemplate?.id ?? 'no-template'}>
          <div className={styles.uploadTemplates__titleContainer}>
            <CoreTypography
              variant={CoreTypographyVariant.Title03Medium}
            >{`${t('input.systemPackageFiles.helperText')} ${currentIndex + 1}`}</CoreTypography>
            {idx !== undefined && (
              <CoreButton onClick={removeTemplate(idx)} size="small">
                <CloseIcon />
              </CoreButton>
            )}
          </div>
          {verifyTemplatesFilesWereUploaded(field.value?.[currentIndex]?.template, idx) ||
          !!field.value?.[currentIndex]?.targetLocation ? (
            <TextFieldInputFwd
              data-testid="target-location"
              disabled={disabled}
              error={!!(fieldState?.error as unknown as TargetLocationError[])?.[currentIndex]?.targetLocation}
              fullWidth
              helperText={toString(
                (fieldState?.error as unknown as TargetLocationError[])?.[currentIndex]?.targetLocation?.message,
              )}
              label={t('input.systemPackageFiles.label')}
              onChange={handleChangeTargetLocationField(idx)}
              required={isTargetFieldRequired(field.value?.[currentIndex])}
              value={toString(field.value?.[currentIndex]?.targetLocation)}
              variant="filled"
              slotProps={{
                inputLabel: { shrink: true },
                htmlInput: { readOnly: disabled, 'data-testid': 'field-target-location' },
              }}
            />
          ) : undefined}
          <div className={filePreviewStyles.fileControlWrapper}>
            <FileSelect
              fileConfiguration={{
                isMultiFiles: false,
                autoCrop: true,
                maxFileSize,
                fileExtensions,
                minFileSize: 1,
                maxFiles: 1,
              }}
              filesToUpload={getUploadedFiles(idx)}
              onFilesChange={handleFilesChange(idx)}
              hasError={getParameterIsInvalid(idx)}
              getUploadingProgress={getUploadingProgress}
              shouldProcessBase64={false}
              selectMainTitleText={t('field.upload.selectMainTitleText', { ns: ALL_LNGS_NAMESPACES.COMMON })}
              selectFormatsText={t('field.upload.selectFormatsText', {
                ns: ALL_LNGS_NAMESPACES.COMMON,
                formats: fileExtensions.join(', '),
              })}
              selectSizeText={t('field.upload.selectSizeText', {
                ns: ALL_LNGS_NAMESPACES.COMMON,
                size: displayedSize,
              })}
              selectBrowseText={t('field.upload.selectBrowseText', { ns: ALL_LNGS_NAMESPACES.COMMON })}
              globalErrorMessageText={getParameterIsInvalid(idx) ? toString(fieldState.error?.message) : undefined}
            />
          </div>
        </div>
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      t,
      removeTemplate,
      field.value,
      disabled,
      fieldState.error,
      handleChangeTargetLocationField,
      isTargetFieldRequired,
      maxFileSize,
      fileExtensions,
      getUploadedFiles,
      handleFilesChange,
      getParameterIsInvalid,
      displayedSize,
    ],
  );

  return (
    <FormControl error={fieldState.invalid} required>
      <div className={styles.uploadTemplates__container}>
        {field.value.length > 0
          ? field.value.map((value, idx) => {
              return getTemplateInfo(idx, value);
            })
          : getTemplateInfo()}
      </div>
      <DeviceAppVersionUploadSummary
        hideSize={field.value.length <= 0}
        onAdd={handleAddTemplateConfig}
        size={getSumUploadedTemplates(field.value, t)}
      />
    </FormControl>
  );
}

export function SystemPackageVersionDeploymentRulesField({
  control,
  applicationFiles,
  triggerValidation,
}: SystemPackageVersionDeploymentRulesFieldProps): ReactNode {
  const { t } = useTranslation([ALL_LNGS_NAMESPACES.COMMON, ALL_LNGS_NAMESPACES.APPLICATION]);

  const selectedExistingFilesController = useController({ name: 'build.existingAppFiles', control });
  const inputedApplicationFilesController = useController({ name: 'build.applicationFiles', control });

  const applicationFilesResource = useMemo<SystemPackageVersionDeploymentRulesFieldResource[]>(
    () =>
      selectedExistingFilesController.field.value
        ?.map(({ value, order, downloadPolicy }) => {
          const fileInfo = applicationFiles.find(({ id }) => id === value);
          return fileInfo
            ? {
                name: fileInfo.name,
                size: fileInfo.size,
                id: fileInfo.id,
                order,
                downloadPolicy,
              }
            : undefined;
        })
        .filter(Boolean),
    [applicationFiles, selectedExistingFilesController.field.value],
  );

  const inputFilesResource = useMemo<SystemPackageVersionDeploymentRulesFieldResource[]>(
    () =>
      inputedApplicationFilesController.field.value?.reduce(
        (accum, current) => [
          ...accum,
          ...current.template.map((fileInfo) => ({
            name: fileInfo.name,
            size: fileInfo.size,
            id: current.id,
            order: fileInfo.order,
            downloadPolicy: fileInfo.downloadPolicy,
            fieldId: fileInfo.id,
          })),
        ],
        [] as SystemPackageVersionDeploymentRulesFieldResource[],
      ),
    [inputedApplicationFilesController.field.value],
  );

  // =========================================================

  const data = useMemo(
    () =>
      [
        ...(applicationFilesResource?.map(({ downloadPolicy, ...field }) => ({
          ...field,
          type: 'existingAppFiles',
          downloadPolicy: downloadPolicy ? downloadPolicy.map((data, index) => ({ ...data, order: index })) : undefined,
        })) ?? []),
        ...(inputFilesResource?.map(({ downloadPolicy, ...field }) => ({
          ...field,
          type: 'applicationFiles',
          downloadPolicy: downloadPolicy ? downloadPolicy.map((data, index) => ({ ...data, order: index })) : undefined,
        })) ?? []),
      ]
        .map((field) => {
          const downloadPolicy = getDeploymentRulesFieldError({
            id: field.fieldId ?? field.id,
            type: field.type as 'existingAppFiles' | 'applicationFiles',
            existingFilesError: selectedExistingFilesController.fieldState.error,
            applicationFilesError: inputedApplicationFilesController.fieldState.error,
            downloadPolicy: field.downloadPolicy,
          });
          return { ...field, downloadPolicy };
        })
        .sort((current, next) => current?.order - next?.order),
    [
      applicationFilesResource,
      inputFilesResource,
      inputedApplicationFilesController.fieldState.error,
      selectedExistingFilesController.fieldState.error,
    ],
  );

  const handleAddRule = useCallback(
    (id: string, type: 'existingAppFiles' | 'applicationFiles') => () => {
      if (type === 'existingAppFiles') {
        selectedExistingFilesController.field.onChange(
          selectedExistingFilesController.field.value.map((file) =>
            file.value === id
              ? {
                  ...file,
                  downloadPolicy: [...(file.downloadPolicy ? file.downloadPolicy : []), { name: '' }],
                }
              : file,
          ),
        );
      } else {
        inputedApplicationFilesController.field.onChange(
          inputedApplicationFilesController.field.value.map((appFile) => {
            if (appFile.id === id) {
              return {
                ...appFile,
                template: appFile.template.map((file) => {
                  return {
                    ...file,
                    downloadPolicy: [...(file.downloadPolicy ? file.downloadPolicy : []), { name: '' }],
                  };
                }),
              };
            } else {
              return appFile;
            }
          }),
        );
      }
    },
    [selectedExistingFilesController.field, inputedApplicationFilesController.field],
  );

  const handleDeleteRule = useCallback(
    (id: string, idx: number, type: 'existingAppFiles' | 'applicationFiles') => () => {
      if (type === 'existingAppFiles') {
        selectedExistingFilesController.field.onChange(
          selectedExistingFilesController.field.value.map((file) => {
            if (file.value === id) {
              const downloadPolicy = file.downloadPolicy?.filter((_, index) => index !== idx);
              return {
                ...file,
                downloadPolicy: downloadPolicy.length ? downloadPolicy : undefined,
              };
            } else {
              return file;
            }
          }),
        );
      } else {
        inputedApplicationFilesController.field.onChange(
          inputedApplicationFilesController.field.value.map((appFile) => {
            if (appFile.id === id) {
              return {
                ...appFile,
                template: appFile.template.map((file) => {
                  const downloadPolicy = file.downloadPolicy?.filter((_, index) => index !== idx);
                  return {
                    ...file,
                    downloadPolicy: downloadPolicy.length ? downloadPolicy : undefined,
                  };
                }),
              };
            } else {
              return appFile;
            }
          }),
        );
      }
      triggerValidation?.();
    },
    [inputedApplicationFilesController.field, selectedExistingFilesController.field, triggerValidation],
  );

  const handleChangeFieldValue = useCallback(
    (id: string, idx: number, type: 'existingAppFiles' | 'applicationFiles') =>
      (evt: ChangeEvent<HTMLInputElement>) => {
        if (type === 'existingAppFiles') {
          selectedExistingFilesController.field.onChange(
            selectedExistingFilesController.field.value.map((file) => {
              if (file.value === id) {
                const downloadPolicy = file.downloadPolicy?.map((policy, index) =>
                  index === idx ? { ...policy, name: evt.target.value } : policy,
                );
                return {
                  ...file,
                  downloadPolicy,
                };
              } else {
                return file;
              }
            }),
          );
        } else {
          inputedApplicationFilesController.field.onChange(
            inputedApplicationFilesController.field.value.map((appFile) => {
              if (appFile.id === id) {
                return {
                  ...appFile,
                  template: appFile.template.map((file) => {
                    return {
                      ...file,
                      downloadPolicy: file.downloadPolicy?.map((policy, index) =>
                        index === idx && appFile.id === id ? { ...policy, name: evt.target.value } : policy,
                      ),
                    };
                  }),
                };
              } else {
                return appFile;
              }
            }),
          );
        }
        triggerValidation?.();
      },
    [selectedExistingFilesController.field, inputedApplicationFilesController.field, triggerValidation],
  );

  return (
    <div className={styles.systemPackageVersionDeploymentRules__container}>
      {data.map(({ id, name, size, downloadPolicy, type }) => (
        <div key={id} className={styles.systemPackageVersionDeploymentRules__item}>
          <div className={styles.systemPackageVersionDeploymentRules__header}>
            <div className={styles.systemPackageVersionDeploymentRules__labelsContainer}>
              <DeviceAppVersionFilesList
                readOnly
                items={[{ filename: name, fileInfo: `${getHumanizedFileSize(size, t)}${templateInfoJoiner}` }]}
              />
            </div>
            <div className={styles.systemPackageVersionDeploymentRules__addDeploymentRuleBtn__container}>
              <CoreButton
                variant="text"
                size="small"
                onClick={handleAddRule(id, type as 'existingAppFiles' | 'applicationFiles')}
                disabled={!!downloadPolicy}
              >
                {`+ ${t('button.deploymentRule', { ns: ALL_LNGS_NAMESPACES.APPLICATION })}`}
              </CoreButton>
            </div>
          </div>
          {downloadPolicy?.length && (
            <div className={styles.systemPackageVersionDeploymentRules__uninstallRulesContainer}>
              <div className={styles.systemPackageVersionDeploymentRules__uninstallRulesContainer__header}>
                <CoreTypography variant={CoreTypographyVariant.Body03Bold}>
                  {t('title.uninstallApplication', { ns: ALL_LNGS_NAMESPACES.APPLICATION })}
                </CoreTypography>
                <CoreTypography variant={CoreTypographyVariant.Label01Regular}>
                  {t('subtitle.uninstallApplication', { ns: ALL_LNGS_NAMESPACES.APPLICATION })}
                </CoreTypography>
              </div>
              {downloadPolicy.map(({ order: policyOrder, name: policyName, error, helperText }, idx) => (
                <div
                  className={styles.systemPackageVersionDeploymentRules__uninstallRulesContainer__fieldContainer}
                  key={`policy-${id}`}
                >
                  <TextFieldInputFwd
                    data-testid={`policy-name-${policyName}`}
                    fullWidth
                    label={`Application name`}
                    onChange={handleChangeFieldValue(id, idx, type as 'existingAppFiles' | 'applicationFiles')}
                    value={policyName}
                    variant="filled"
                    slotProps={{
                      inputLabel: { shrink: true },
                      htmlInput: { 'data-testid': `field-target-location-${policyOrder}` },
                    }}
                    required
                    error={error}
                    helperText={helperText}
                    placeholder={t('input.deploymentRules.placeholder', { ns: ALL_LNGS_NAMESPACES.APPLICATION })}
                  />
                  <CoreButton
                    variant="text"
                    onClick={handleDeleteRule(id, idx, type as 'existingAppFiles' | 'applicationFiles')}
                  >
                    <DeleteOutlinedIcon className={styles.systemPackageVersionDeploymentRules__deleteIcon} />
                  </CoreButton>
                </div>
              ))}
              {downloadPolicy?.length && (
                <div className={styles.systemPackageVersionDeploymentRules__uninstallRulesContainer__footer}>
                  <CoreButton
                    variant="text"
                    size="small"
                    onClick={handleAddRule(id, type as 'existingAppFiles' | 'applicationFiles')}
                    disabled={downloadPolicy.some((policy) => isEmpty(policy.name))}
                  >
                    {`+ ${t('button.addDeploymentRuleApplication', { ns: ALL_LNGS_NAMESPACES.APPLICATION })}`}
                  </CoreButton>
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
